import React, { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import Turnstile from "react-turnstile";

import { cn } from "@/lib/utils";
import { logger } from "@/utils/logger/logger";

import type { CaptchaResponse, CaptchaWidgetProps } from "../index";

const MAX_RETRY_ATTEMPTS = 3; // Define a maximum number of retries

const AuthChallengeWidget: React.FC<CaptchaWidgetProps> = ({
  config,
  onCaptchaResponse,
  className = "",
  theme,
  error,
  control,
  name,
  rules,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | undefined>(error);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const retryCount = useRef(0);

  // Update internal error if external error prop changes
  useEffect(() => {
    setErrorMessage(error);
  }, [error]);

  if (config.provider !== "auth0_v2") {
    return null;
  }

  const siteKey = config.siteKey;

  if (!siteKey) {
    return null;
  }

  const handleVerify = (token: string) => {
    logger.log("[Captcha] Verification successful");
    retryCount.current = 0;
    setErrorMessage(undefined);
    setIsRefreshing(false);

    const response: CaptchaResponse = {
      provider: "auth0_v2",
      token: token,
    };
    onCaptchaResponse(response);
  };

  const handleError = (error: string) => {
    onCaptchaResponse(null);

    if (retryCount.current < MAX_RETRY_ATTEMPTS) {
      retryCount.current += 1;
      return;
    } else {
      setErrorMessage(`Verification failed after multiple attempts: ${error}`);
    }
  };

  const handleExpire = () => {
    logger.log("[Captcha] Token expired - will request new on interaction");
    onCaptchaResponse(null);
    retryCount.current = 0;
    setIsRefreshing(true);
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={rules}
      render={({ field }) => (
        <div className={cn("space-y-2", className)}>
          <div
            className={cn(
              "rounded-sm w-full overflow-hidden",
              errorMessage ? "border border-[#d93025]" : "",
            )}
          >
            <Turnstile
              sitekey={siteKey}
              onVerify={(token) => {
                logger.log("[Captcha] Token received");
                field.onChange(token);
                handleVerify(token);
              }}
              onError={(err) => {
                logger.error("[Captcha] Error", err);
                field.onChange("");
                handleError(err);
              }}
              onExpire={() => {
                logger.log("[Captcha] Token expired");
                field.onChange("");
                handleExpire();
              }}
              theme={theme}
              size="flexible"
              refreshExpired="auto"
            />
          </div>
          {isRefreshing && <div className="text-xs text-gray-600">Refreshing verification...</div>}
        </div>
      )}
    />
  );
};

export default AuthChallengeWidget;
